import { useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { z } from 'zod';
import { RootState } from '../reducers';
import { addTagToUserThunk } from '../features/settings/settingsSlice';

// Define the feature flag schema
export const featureFlagSchema = z.object({
  feature_enable_onboarding: z.enum(['true', 'false']),
  feature_unguard_upload: z.enum(['true', 'false']),
  feature_unguard_import_url: z.enum(['true', 'false']),
  feature_support_variant_ai: z.enum(['true', 'false']), // Currently disabled - using team tags instead
  feature_hide_unlimited: z.enum(['true', 'false']),
  feature_chevron_below_price: z.enum(['true', 'false']),
  feature_hide_upgrade_left_column: z.enum(['true', 'false']),
  feature_higher_free_limits: z.enum(['true', 'false']),
  feature_show_compare_plans: z.enum(['true', 'false']),
  feature_show_trial_modal: z.enum(['true', 'false']), // Enabled for all users
  feature_7_day_trial: z.enum(['true', 'false']), // Enabled for all users - 7-day trial
  feature_pricing_19pm: z.enum(['true', 'false']), // DEPRECATED - data corrupted, use v2
  feature_pricing_19pm_v2: z.enum(['true', 'false']), // Pricing A/B test v2 - 50% see $19/mo, 50% see $14/mo
  feature_payment_verification: z.enum(['true', 'false']), // $1 authorization hold during trial signup
});

// Derive TypeScript types from Zod schema
export type FeatureFlagKey = keyof z.infer<typeof featureFlagSchema>;
export type FeatureFlagValue = z.infer<
  typeof featureFlagSchema
>[FeatureFlagKey];

// Helper function to generate random boolean
const getRandomBoolean = () => Math.random() >= 0.5;

// Helper function to check if a key is a feature flag
const isFeatureFlag = (key: string): key is FeatureFlagKey => {
  return key.startsWith('feature_');
};

// Cache for pending feature flag assignments to prevent race conditions
// This ensures consistent values even if component re-renders before Redux updates
const pendingFlagAssignments = new Map<string, boolean>();

export const useBooleanFeature = () => {
  const dispatch = useDispatch();
  const tags = useSelector((state: RootState) => state.settings.user.tags);

  const getFeature = useCallback(
    (key: string): boolean => {
      // Fixed feature flags - these are hardcoded values
      if (key === 'feature_enable_onboarding') {
        return false;
      }

      if (key === 'feature_hide_unlimited') {
        return false;
      }

      if (key === 'feature_unguard_upload') {
        return false;
      }

      if (key === 'feature_unguard_import_url') {
        return false;
      }

      if (key === 'feature_higher_free_limits') {
        return false;
      }

      if (key === 'feature_chevron_below_price') {
        return false;
      }

      if (key === 'feature_hide_upgrade_left_column') {
        return false;
      }

      if (key === 'feature_show_compare_plans') {
        return false;
      }

      if (key === 'feature_show_trial_modal') {
        return true;
      }

      if (key === 'feature_7_day_trial') {
        return true;
      }

      // If it's a defined feature flag
      if (isFeatureFlag(key)) {
        // If the feature flag exists in tags, return its value
        if (key in tags) {
          // Clean up pending cache once value is in Redux
          pendingFlagAssignments.delete(key);
          return tags[key] === 'true';
        }

        // Check pending cache to prevent race conditions during rapid re-renders
        if (pendingFlagAssignments.has(key)) {
          return pendingFlagAssignments.get(key)!;
        }

        // Feature flags that should default to OFF (not random)
        // These can still be toggled ON via the Features settings page
        const defaultOffFlags: FeatureFlagKey[] = [];

        // If the feature flag doesn't exist, calculate initial value
        const randomValue = defaultOffFlags.includes(key as FeatureFlagKey)
          ? false
          : getRandomBoolean();

        // Cache the value to ensure consistency before Redux updates
        pendingFlagAssignments.set(key, randomValue);

        const stringValue = randomValue.toString() as FeatureFlagValue;
        dispatch(addTagToUserThunk({ key, value: stringValue }));
        return randomValue;
      }

      // For undefined feature flags, return random boolean without storing
      return getRandomBoolean();
    },
    [dispatch, tags],
  );

  return { getFeature };
};

export default useBooleanFeature;
